import React, { useMemo } from 'react';
import { COLORS } from '../constants/theme';

/**
 * Data Insights Component
 * Displays histograms and analysis of spot price data
 */
const DataInsights = ({ priceData }) => {
  // Calculate price statistics and distributions
  const insights = useMemo(() => {
    if (!priceData || priceData.length === 0) return null;

    const prices = priceData.filter(p => p !== null && p !== undefined && !isNaN(p));

    // Basic stats
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const avg = prices.reduce((sum, p) => sum + p, 0) / prices.length;
    const median = [...prices].sort((a, b) => a - b)[Math.floor(prices.length / 2)];

    // Standard deviation
    const variance = prices.reduce((sum, p) => sum + Math.pow(p - avg, 2), 0) / prices.length;
    const stdDev = Math.sqrt(variance);

    // Price distribution (histogram buckets)
    const bucketSize = 50; // DKK/MWh per bucket
    const buckets = {};
    prices.forEach(price => {
      const bucket = Math.floor(price / bucketSize) * bucketSize;
      buckets[bucket] = (buckets[bucket] || 0) + 1;
    });

    // Convert to array and sort
    const histogram = Object.entries(buckets)
      .map(([bucket, count]) => ({
        min: parseInt(bucket),
        max: parseInt(bucket) + bucketSize,
        count,
        percentage: (count / prices.length) * 100
      }))
      .sort((a, b) => a.min - b.min);

    // Price categories
    const categories = {
      negative: prices.filter(p => p < 0).length,
      veryCheap: prices.filter(p => p >= 0 && p < 100).length,
      cheap: prices.filter(p => p >= 100 && p < 200).length,
      normal: prices.filter(p => p >= 200 && p < 300).length,
      elevated: prices.filter(p => p >= 300 && p < 400).length,
      high: prices.filter(p => p >= 400 && p < 500).length,
      veryHigh: prices.filter(p => p >= 500).length,
    };

    // Best buying opportunities
    const sortedPrices = [...prices].sort((a, b) => a - b);
    const top10PercentThreshold = sortedPrices[Math.floor(prices.length * 0.1)];
    const bottom10PercentThreshold = sortedPrices[Math.floor(prices.length * 0.9)];

    return {
      min,
      max,
      avg,
      median,
      stdDev,
      histogram,
      categories,
      totalHours: prices.length,
      top10PercentThreshold,
      bottom10PercentThreshold,
    };
  }, [priceData]);

  if (!insights) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        color: COLORS.ui.text.secondary
      }}>
        Ingen prisdata tilgængelig
      </div>
    );
  }

  const maxCount = Math.max(...insights.histogram.map(h => h.count));

  return (
    <div style={{
      background: COLORS.ui.surface,
      borderRadius: '0.75rem',
      padding: '1.5rem',
      border: `1px solid ${COLORS.ui.border}`,
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    }}>
      <h2 style={{
        margin: '0 0 1.5rem 0',
        color: COLORS.ui.text.primary,
        fontSize: '1.5rem',
        fontWeight: 'bold',
      }}>
        📊 Spotpris Analyse
      </h2>

      {/* Key Statistics */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem',
      }}>
        <StatCard label="Gennemsnit" value={`${insights.avg.toFixed(0)} DKK`} color={COLORS.status.info} />
        <StatCard label="Median" value={`${insights.median.toFixed(0)} DKK`} color={COLORS.status.info} />
        <StatCard label="Minimum" value={`${insights.min.toFixed(0)} DKK`} color={COLORS.status.success} />
        <StatCard label="Maksimum" value={`${insights.max.toFixed(0)} DKK`} color={COLORS.status.error} />
        <StatCard label="Std. afvigelse" value={`±${insights.stdDev.toFixed(0)} DKK`} color={COLORS.status.warning} />
        <StatCard label="Total timer" value={insights.totalHours.toLocaleString()} color={COLORS.ui.text.secondary} />
      </div>

      {/* Price Categories */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{
          margin: '0 0 1rem 0',
          color: COLORS.ui.text.primary,
          fontSize: '1.1rem',
          fontWeight: '600',
        }}>
          Priskategorier
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <CategoryBar
            label="Negativ (< 0)"
            count={insights.categories.negative}
            total={insights.totalHours}
            color="#10b981"
          />
          <CategoryBar
            label="Meget billig (0-100)"
            count={insights.categories.veryCheap}
            total={insights.totalHours}
            color="#3b82f6"
          />
          <CategoryBar
            label="Billig (100-200)"
            count={insights.categories.cheap}
            total={insights.totalHours}
            color="#6366f1"
          />
          <CategoryBar
            label="Normal (200-300)"
            count={insights.categories.normal}
            total={insights.totalHours}
            color="#8b5cf6"
          />
          <CategoryBar
            label="Forhøjet (300-400)"
            count={insights.categories.elevated}
            total={insights.totalHours}
            color="#f59e0b"
          />
          <CategoryBar
            label="Høj (400-500)"
            count={insights.categories.high}
            total={insights.totalHours}
            color="#ef4444"
          />
          <CategoryBar
            label="Meget høj (>500)"
            count={insights.categories.veryHigh}
            total={insights.totalHours}
            color="#dc2626"
          />
        </div>
      </div>

      {/* Histogram */}
      <div>
        <h3 style={{
          margin: '0 0 1rem 0',
          color: COLORS.ui.text.primary,
          fontSize: '1.1rem',
          fontWeight: '600',
        }}>
          Prisfordeling (histogram)
        </h3>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.25rem',
          maxHeight: '400px',
          overflowY: 'auto',
        }}>
          {insights.histogram.map(bucket => (
            <div
              key={bucket.min}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
              }}
            >
              <div style={{
                minWidth: '120px',
                fontSize: '0.75rem',
                color: COLORS.ui.text.secondary,
                textAlign: 'right',
              }}>
                {bucket.min}-{bucket.max} DKK
              </div>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div
                  style={{
                    width: `${(bucket.count / maxCount) * 100}%`,
                    height: '20px',
                    background: getColorForBucket(bucket.min),
                    borderRadius: '2px',
                    transition: 'width 0.3s ease',
                  }}
                />
                <span style={{
                  fontSize: '0.75rem',
                  color: COLORS.ui.text.secondary,
                  minWidth: '60px',
                }}>
                  {bucket.count} timer ({bucket.percentage.toFixed(1)}%)
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Optimization Tips */}
      <div style={{
        marginTop: '2rem',
        padding: '1rem',
        background: COLORS.ui.background,
        borderRadius: '0.5rem',
        border: `1px solid ${COLORS.ui.border}`,
      }}>
        <h4 style={{
          margin: '0 0 0.75rem 0',
          color: COLORS.status.info,
          fontSize: '1rem',
          fontWeight: '600',
        }}>
          💡 Optimeringspotentiale
        </h4>
        <ul style={{
          margin: 0,
          padding: '0 0 0 1.5rem',
          color: COLORS.ui.text.secondary,
          fontSize: '0.875rem',
          lineHeight: '1.6',
        }}>
          <li>
            <strong>Bedste købsvindue:</strong> Under {insights.top10PercentThreshold.toFixed(0)} DKK/MWh
            (10% billigste timer)
          </li>
          <li>
            <strong>Undgå indkøb over:</strong> {insights.bottom10PercentThreshold.toFixed(0)} DKK/MWh
            (10% dyreste timer)
          </li>
          <li>
            <strong>Prispread:</strong> {(insights.max - insights.min).toFixed(0)} DKK/MWh
            giver stort arbitragepotentiale
          </li>
          <li>
            <strong>Anbefaling:</strong> Fyld HTES under {insights.top10PercentThreshold.toFixed(0)} DKK/MWh,
            udlad turbine over {insights.bottom10PercentThreshold.toFixed(0)} DKK/MWh
          </li>
        </ul>
      </div>
    </div>
  );
};

// Helper Components
const StatCard = ({ label, value, color }) => (
  <div style={{
    padding: '1rem',
    background: COLORS.ui.background,
    borderRadius: '0.5rem',
    border: `1px solid ${COLORS.ui.border}`,
  }}>
    <div style={{
      fontSize: '0.75rem',
      color: COLORS.ui.text.secondary,
      marginBottom: '0.25rem',
    }}>
      {label}
    </div>
    <div style={{
      fontSize: '1.25rem',
      fontWeight: 'bold',
      color,
    }}>
      {value}
    </div>
  </div>
);

const CategoryBar = ({ label, count, total, color }) => {
  const percentage = (count / total) * 100;
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    }}>
      <div style={{
        minWidth: '180px',
        fontSize: '0.875rem',
        color: COLORS.ui.text.primary,
      }}>
        {label}
      </div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <div style={{
          flex: 1,
          height: '24px',
          background: COLORS.ui.border,
          borderRadius: '12px',
          overflow: 'hidden',
        }}>
          <div
            style={{
              width: `${percentage}%`,
              height: '100%',
              background: color,
              transition: 'width 0.3s ease',
            }}
          />
        </div>
        <span style={{
          fontSize: '0.875rem',
          color: COLORS.ui.text.secondary,
          minWidth: '100px',
          fontWeight: '500',
        }}>
          {count} timer ({percentage.toFixed(1)}%)
        </span>
      </div>
    </div>
  );
};

// Get color based on price bucket
function getColorForBucket(minPrice) {
  if (minPrice < 0) return '#10b981';  // Green for negative
  if (minPrice < 100) return '#3b82f6';  // Blue for very cheap
  if (minPrice < 200) return '#6366f1';  // Indigo for cheap
  if (minPrice < 300) return '#8b5cf6';  // Purple for normal
  if (minPrice < 400) return '#f59e0b';  // Amber for elevated
  if (minPrice < 500) return '#ef4444';  // Red for high
  return '#dc2626';  // Dark red for very high
}

export default DataInsights;
